import { useState, useEffect } from "react";

type TabItem = Record<string, unknown>;

interface UsePaginationReturn {
  currentPage: number;
  setCurrentPage: (page: number) => void;
  itemsPerPage: number;
  setItemsPerPage: (items: number) => void;
  totalPages: number;
  paginatedTabOne: TabItem[];
  paginatedTabTwo: TabItem[];
  handlePageChange: (page: number) => void;
  handleItemsPerPageChange: (
    event: React.ChangeEvent<HTMLSelectElement>
  ) => void;
}

/**
 * Custom hook for managing pagination state and logic
 * @param tabOne - Array of items for the first tab
 * @param tabTwo - Array of items for the second tab
 * @returns Pagination state and control methods
 * @example
 * const { currentPage, paginatedTabOne, handlePageChange } = usePagination(items1, items2)
 */
export function usePagination(
  tabOne: TabItem[],
  tabTwo: TabItem[]
): UsePaginationReturn {
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage, setItemsPerPage] = useState<number>(9);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [paginatedTabOne, setPaginatedTabOne] = useState<TabItem[]>([]);
  const [paginatedTabTwo, setPaginatedTabTwo] = useState<TabItem[]>([]);

  useEffect(() => {
    const storedPage = sessionStorage.getItem("currentPage");
    const storedItemsPerPage = sessionStorage.getItem("itemsPerPage");

    if (storedPage) {
      setCurrentPage(parseInt(storedPage, 10));
    }
    if (storedItemsPerPage) {
      setItemsPerPage(parseInt(storedItemsPerPage, 10));
    }
  }, []);

  useEffect(() => {
    const totalItems = tabOne.length + tabTwo.length;
    setTotalPages(Math.ceil(totalItems / itemsPerPage));

    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;

    if (startIndex < tabOne.length) {
      const tabOneEndIndex = Math.min(endIndex, tabOne.length);
      setPaginatedTabOne(tabOne.slice(startIndex, tabOneEndIndex));

      const remainingSlots = itemsPerPage - (tabOneEndIndex - startIndex);
      if (remainingSlots > 0) {
        setPaginatedTabTwo(tabTwo.slice(0, remainingSlots));
      } else {
        setPaginatedTabTwo([]);
      }
    } else {
      const tabTwoStartIndex = startIndex - tabOne.length;
      const tabTwoEndIndex = Math.min(
        tabTwoStartIndex + itemsPerPage,
        tabTwo.length
      );
      setPaginatedTabOne([]);
      setPaginatedTabTwo(tabTwo.slice(tabTwoStartIndex, tabTwoEndIndex));
    }
  }, [tabOne, tabTwo, currentPage, itemsPerPage]);

  const handlePageChange = (page: number): void => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      sessionStorage.setItem("currentPage", page.toString());
    }
  };

  const handleItemsPerPageChange = (
    event: React.ChangeEvent<HTMLSelectElement>
  ): void => {
    const newItemsPerPage = parseInt(event.target.value, 10);
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
    sessionStorage.setItem("itemsPerPage", newItemsPerPage.toString());
  };

  return {
    currentPage,
    setCurrentPage,
    itemsPerPage,
    setItemsPerPage,
    totalPages,
    paginatedTabOne,
    paginatedTabTwo,
    handlePageChange,
    handleItemsPerPageChange,
  };
}

export default usePagination;
